import { Core } from "@faire/design-tokens";
import { ISuggestionEntry } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import { ResultDropdownDeleteButton } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultDropdownDeleteButton";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Opacity } from "@faire/web--source/slate/Color";
import { Label } from "@faire/web--source/slate/Filter/styled";
import { Flex } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import * as React from "react";
import type { JSX } from "react";
import styled from "styled-components";

interface IProps {
  suggestion: ISuggestionEntry;
  onHover: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  selected: boolean;
  // To show 'x', pass onDelete
  onDelete?: () => void;
  // To show loading spinner in place of 'x', pass isDeleting as true
  isDeleting?: boolean;
  svgIcon?: JSX.Element;
  url: string;
  dataTestId: string;
}

export const ImageSearchPill: React.FC<IProps> = ({
  suggestion,
  onClick,
  onHover,
  onMouseLeave,
  selected,
  onDelete,
  isDeleting = false,
  svgIcon,
  url,
  dataTestId,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const isSSR = useIsSSR();
  return (
    <Pill
      selected={selected}
      onMouseEnter={onHover}
      onMouseLeave={onMouseLeave}
      onClick={isDeleting ? undefined : onClick}
      to={url}
      data-test-id={dataTestId}
    >
      {svgIcon ? (
        <SVGIconWrapper color={Core.icon.default}>{svgIcon}</SVGIconWrapper>
      ) : (
        <Avatar
          $avatarImageUrl={
            optimizeImageUrl(
              { url: suggestion.searchData?.image?.url },
              isSSR,
              { height: 32, width: 32, resizeMethod: "fill" }
            ) ?? ""
          }
        />
      )}
      <Label>{suggestion.label}</Label>

      {onDelete ? (
        <StyledDeleteButton
          onDelete={onDelete}
          isDeleting={isDeleting}
          ariaLabel={strictLocalize(
            {
              defaultMessage:
                'Delete "{labelFullyTranslatedSentence}" search query',
              description: {
                text: "Prompt to remove a saved search term from history",
              },
            },
            { labelFullyTranslatedSentence: suggestion.label }
          )}
        />
      ) : (
        <Spacer size="2x" />
      )}
    </Pill>
  );
};

const Pill = styled(Link)<{
  selected: boolean;
}>`
  display: flex;
  align-items: center;
  border-radius: 20px;
  cursor: pointer;
  height: 40px;
  margin: 4px;
  background-color: ${({ selected }) =>
    selected ? Core.surface.subdued : Core.surface.subdued + Opacity.O_50};
  &:hover {
    background-color: ${Core.surface.subdued};
  }
  &:focus-visible {
    outline: auto; /* Support Firefox & non-webkit browsers */
    outline: 2px solid -webkit-focus-ring-color;
    outline-offset: 2px;
  }
`;

const Avatar = styled.div<{
  $avatarImageUrl: string;
}>`
  width: 32px;
  height: 32px;
  margin: 4px 8px 4px 4px;
  border-radius: 100%;
  background-position: center;
  background-size: cover;
  background-image: url(${(props) => props.$avatarImageUrl});
`;
const StyledDeleteButton = styled(ResultDropdownDeleteButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${getSpacing(0, "1x")};
  border-radius: 50%;
  height: 28px;
  width: 28px;

  &:hover {
    background-color: ${Core.surface.subdued};
  }
`;

const SVGIconWrapper = styled(Flex).attrs({
  justify: "center",
  align: "center",
})`
  height: 28px;
  width: 28px;
  margin: ${getSpacing(0, "1x")};
  border-radius: 50%;
`;
